import { useState } from 'react'
// import { WaitlistModal } from './waitlist-modal'

type PricingCategory = 'transactional' | 'marketing'
type BillingInterval = 'monthly' | 'annual'

interface Tier {
  readonly name: string
  readonly monthly: number
  readonly annual: number
  readonly emails: string
  readonly contacts: string
  readonly overage: string
  readonly pop: boolean
  readonly features: readonly string[]
  readonly highlight?: string
  readonly gradient: string
  readonly border: string
}

const TRANSACTIONAL_TIERS: readonly Tier[] = [
  {
    name: 'Starter', monthly: 5, annual: 4, emails: '25,000', contacts: '25,000', overage: '$0.40/1K', pop: false,
    gradient: 'linear-gradient(165deg, #FEFCFA 0%, #F7F3EF 100%)',
    border: '#EDE9E4',
    features: [
      '2 domains',
      '10 req/s rate limit',
      'Auto suppression lists',
      '7-day analytics',
      '2 webhook endpoints',
      'Email support',
    ],
  },
  {
    name: 'Pro', monthly: 18, annual: 15, emails: '75,000', contacts: '75,000', overage: '$0.30/1K', pop: true,
    gradient: 'linear-gradient(165deg, #1a1216 0%, #2a1f24 50%, #1a1216 100%)',
    border: '#FF6B00',
    highlight: 'Everything in Starter, plus:',
    features: [
      '10 domains',
      '50 req/s rate limit',
      'Priority send queue',
      'Slack alerts',
      '30-day analytics',
      '3 team members',
    ],
  },
  {
    name: 'Growth', monthly: 45, annual: 36, emails: '300,000', contacts: '300,000', overage: '$0.20/1K', pop: false,
    gradient: 'linear-gradient(165deg, #FDF9F5 0%, #F5EDE5 100%)',
    border: '#E5DDD5',
    highlight: 'Everything in Pro, plus:',
    features: [
      '25 domains',
      '200 req/s rate limit',
      'Dedicated IP add-on ($29)',
      'Auto IP warming',
      '90-day analytics',
      '10 team members',
    ],
  },
  {
    name: 'Scale', monthly: 349, annual: 291, emails: '2,000,000', contacts: '2,000,000', overage: '$0.10/1K', pop: false,
    gradient: 'linear-gradient(165deg, #FBF6F1 0%, #F0E6DA 100%)',
    border: '#DDD2C6',
    highlight: 'Everything in Growth, plus:',
    features: [
      'Unlimited domains',
      '500 req/s rate limit',
      'Dedicated IP add-on ($29)',
      '1-year analytics',
      'Unlimited team members',
      'Slack support channel',
    ],
  },
]

const MARKETING_TIERS: readonly Tier[] = [
  {
    name: 'Starter', monthly: 15, annual: 12, emails: '10,000', contacts: '10,000', overage: '$1.50/1K', pop: false,
    gradient: 'linear-gradient(165deg, #FEFCFA 0%, #F7F3EF 100%)',
    border: '#EDE9E4',
    features: [
      'Contact lists',
      'Drag-and-drop editor',
      'Basic templates',
      '7-day analytics',
      'A/B subject lines',
      'Email support',
    ],
  },
  {
    name: 'Pro', monthly: 59, annual: 49, emails: '50,000', contacts: '50,000', overage: '$1.00/1K', pop: true,
    gradient: 'linear-gradient(165deg, #1a1216 0%, #2a1f24 50%, #1a1216 100%)',
    border: '#FF6B00',
    highlight: 'Everything in Starter, plus:',
    features: [
      'Audience segments',
      'Custom templates',
      'Send-time optimization',
      '30-day analytics',
      'Automation flows',
      '3 team members',
    ],
  },
  {
    name: 'Growth', monthly: 149, annual: 124, emails: '200,000', contacts: '200,000', overage: '$0.75/1K', pop: false,
    gradient: 'linear-gradient(165deg, #FDF9F5 0%, #F5EDE5 100%)',
    border: '#E5DDD5',
    highlight: 'Everything in Pro, plus:',
    features: [
      'Advanced segments',
      'Dynamic content',
      'Multi-step automations',
      '90-day analytics',
      '10 team members',
      'Priority support',
    ],
  },
  {
    name: 'Scale', monthly: 399, annual: 332, emails: '500,000', contacts: '500,000', overage: '$0.50/1K', pop: false,
    gradient: 'linear-gradient(165deg, #FBF6F1 0%, #F0E6DA 100%)',
    border: '#DDD2C6',
    highlight: 'Everything in Growth, plus:',
    features: [
      'Unlimited segments',
      'Dedicated IP add-on ($29)',
      'Priority sending',
      '1-year analytics',
      'Unlimited team members',
      'Slack support channel',
    ],
  },
]

function CheckIcon({ color }: { readonly color: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3 8.5L6 11.5L13 4.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function PricingSectionB() {
  const [category, setCategory] = useState<PricingCategory>('transactional')
  const [interval, setInterval] = useState<BillingInterval>('monthly')
  // const [waitlistOpen, setWaitlistOpen] = useState(false)
  const tiers = category === 'transactional' ? TRANSACTIONAL_TIERS : MARKETING_TIERS

  return (
    <section
      style={{
        background: '#FDF8F3',
        paddingTop: 100, paddingBottom: 100,
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Subtle bg accent */}
      <div
        style={{
          position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)',
          width: 900, height: 900, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,107,0,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="section-pad" style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p
            style={{
              fontSize: 14, fontWeight: 700, color: '#FF6B00',
              letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12,
            }}
          >
            Simple pricing
          </p>
          <h2 style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-0.03em', color: '#1a1216' }}>
            Pick a plan.{' '}
            <span style={{ fontFamily: "var(--oe-font-serif)", fontStyle: 'italic', color: '#FF6B00' }}>
              Start sending.
            </span>
          </h2>
          <p style={{ color: '#888', fontSize: 16, marginTop: 12 }}>
            No &ldquo;contact sales&rdquo;. No hidden fees. Cancel anytime.
          </p>

          {/* Toggles */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <div
              role="tablist"
              style={{
                display: 'inline-flex',
                background: '#f0ece8', borderRadius: 14, padding: 4,
              }}
            >
              {(['transactional', 'marketing'] as const).map((cat) => (
                <button
                  key={cat}
                  role="tab"
                  aria-selected={category === cat}
                  onClick={() => setCategory(cat)}
                  style={{
                    padding: '10px 24px', borderRadius: 10, border: 'none',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: category === cat ? '#1a1216' : 'transparent',
                    color: category === cat ? '#fff' : '#999',
                  }}
                >
                  {cat === 'transactional' ? 'Transactional' : 'Marketing'}
                </button>
              ))}
            </div>

            <div
              role="tablist"
              style={{
                display: 'inline-flex',
                background: '#f0ece8', borderRadius: 14, padding: 4,
              }}
            >
              {(['monthly', 'annual'] as const).map((int) => (
                <button
                  key={int}
                  role="tab"
                  aria-selected={interval === int}
                  onClick={() => setInterval(int)}
                  style={{
                    padding: '10px 24px', borderRadius: 10, border: 'none',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: interval === int ? '#1a1216' : 'transparent',
                    color: interval === int ? '#fff' : '#999',
                  }}
                >
                  {int === 'monthly' ? 'Monthly' : 'Annual'}
                  {int === 'annual' && (
                    <span style={{ fontSize: 11, color: '#22c55e', marginLeft: 6, fontWeight: 800 }}>
                      -20%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Free tier banner */}
        {(
          <div
            style={{
              background: '#fff', borderRadius: 16,
              border: '1px solid #EDE9E4',
              boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
              padding: '20px 32px', marginBottom: 20,
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px 32px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: '#1a1216' }}>Free</span>
              <span style={{ fontSize: 14, color: '#999' }}>
                {category === 'transactional' ? '10,000 emails/mo' : '3,000 emails/mo'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {(category === 'transactional'
                ? ['1K/day limit', '1 domain', '10,000 contacts', '3-day analytics']
                : ['500/day limit', '1 audience list', '3,000 contacts', '3-day analytics']
              ).map((f) => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckIcon color="#FF6B00" />
                  <span style={{ fontSize: 13, color: '#777' }}>{f}</span>
                </div>
              ))}
            </div>
            <span
              style={{
                marginLeft: 'auto', padding: '10px 24px', borderRadius: 10,
                background: '#f5f1ed', color: '#999',
                border: '1px solid #EDE9E4',
                fontSize: 13, fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              Coming soon
            </span>
          </div>
        )}

        {/* 4-card grid */}
        <div
          className="pricing-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 18,
          }}
        >
          {tiers.map((tier, i) => {
            const price = interval === 'annual' ? tier.annual : tier.monthly
            const isDark = tier.pop
            const textPrimary = isDark ? '#fff' : '#1a1216'
            const textSecondary = isDark ? 'rgba(255,255,255,0.65)' : '#888'
            const textFeature = isDark ? 'rgba(255,255,255,0.8)' : '#666'
            const checkColor = isDark ? '#fff' : '#FF6B00'

            return (
              <div
                key={i}
                style={{
                  background: tier.gradient,
                  borderRadius: 22,
                  padding: '36px 28px 32px',
                  border: `1.5px solid ${tier.border}`,
                  boxShadow: tier.pop
                    ? '0 12px 48px rgba(255, 107, 0, 0.15), 0 4px 16px rgba(0,0,0,0.1)'
                    : '0 4px 20px rgba(0,0,0,0.04)',
                  position: 'relative',
                  display: 'flex', flexDirection: 'column',
                  transition: 'box-shadow 0.3s, transform 0.3s',
                }}
              >
                {tier.pop && (
                  <div
                    style={{
                      position: 'absolute', top: -13, left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#FF6B00', color: '#fff', padding: '5px 20px',
                      borderRadius: 100, fontSize: 11, fontWeight: 800,
                      letterSpacing: '0.06em', whiteSpace: 'nowrap',
                      boxShadow: '0 4px 12px rgba(255,107,0,0.3)',
                    }}
                  >
                    MOST POPULAR
                  </div>
                )}

                {/* Name */}
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14, color: textPrimary }}>
                  {tier.name}
                </div>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginBottom: 2 }}>
                  {interval === 'annual' && (
                    <span
                      style={{
                        fontSize: 20, color: isDark ? 'rgba(255,255,255,0.35)' : '#ccc',
                        textDecoration: 'line-through',
                        fontFamily: 'var(--oe-font-mono)', marginRight: 6,
                      }}
                    >
                      ${tier.monthly}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 46, fontWeight: 800, letterSpacing: '-0.04em',
                      fontFamily: 'var(--oe-font-mono)', color: textPrimary,
                    }}
                  >
                    ${price}
                  </span>
                  <span style={{ color: textSecondary, fontSize: 14 }}>/mo</span>
                </div>

                {/* Volume + overage */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 14, color: textSecondary, fontFamily: 'var(--oe-font-mono)' }}>
                    {tier.emails} emails
                  </div>
                  <div style={{ fontSize: 13, color: textSecondary, fontFamily: 'var(--oe-font-mono)', marginTop: 2 }}>
                    {tier.contacts} contacts
                  </div>
                  <div style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.4)' : '#aaa', marginTop: 3 }}>
                    then {tier.overage} overage
                  </div>
                </div>

                {/* CTA — disabled until launch */}
                <div
                  style={{
                    display: 'block', textAlign: 'center', padding: 14,
                    borderRadius: 12, fontWeight: 700, fontSize: 14,
                    background: isDark ? 'rgba(255,107,0,0.5)' : 'rgba(26,18,22,0.4)',
                    color: 'rgba(255,255,255,0.7)', width: '100%',
                    marginBottom: 24,
                  }}
                >
                  Coming soon
                </div>

                {/* Highlight */}
                {tier.highlight && (
                  <div
                    style={{
                      fontSize: 11, fontWeight: 700,
                      color: isDark ? 'rgba(255,255,255,0.5)' : '#bbb',
                      marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}
                  >
                    {tier.highlight}
                  </div>
                )}

                {/* Features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                  {tier.features.map((f) => (
                    <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ marginTop: 1 }}>
                        <CheckIcon color={checkColor} />
                      </div>
                      <span style={{ fontSize: 13.5, lineHeight: 1.4, color: textFeature }}>
                        {f}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Annual savings */}
                {interval === 'annual' && (
                  <div
                    style={{
                      marginTop: 'auto', paddingTop: 20,
                      fontSize: 12, fontWeight: 700, color: '#22c55e',
                      textAlign: 'center',
                    }}
                  >
                    Save ${(tier.monthly - tier.annual) * 12}/year
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <p style={{ color: '#999', fontSize: 13, fontFamily: 'var(--oe-font-mono)' }}>
            All paid plans include 10% free overage buffer + 3 forgiven overages
          </p>
          <p style={{ color: '#aaa', fontSize: 13, marginTop: 8 }}>
            {category === 'transactional'
              ? 'Marketing and transactional emails are fully isolated — separate IPs, separate queues, separate reputation.'
              : 'Marketing IPs are fully isolated from transactional — one never affects the other.'}
          </p>
          <p style={{ color: '#aaa', fontSize: 13, marginTop: 8 }}>
            Subscribe to each independently. Every account includes the free tier of both.
          </p>
          <p style={{ marginTop: 12 }}>
            <a
              href="/for-builders"
              style={{
                fontSize: 13, fontWeight: 700, color: '#FF6B00',
                textDecoration: 'none', transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8' }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
            >
              Student or startup? Get a discount →
            </a>
          </p>
        </div>
      </div>

      {/* <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} /> */}
    </section>
  )
}
